import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Button,
  Checkbox,
  Dialog,
  DialogSurface,
  DialogBody,
  DialogTitle,
  DialogContent,
  DialogActions,
  Dropdown,
  Input,
  Option,
  Spinner,
  Tab,
  TabList,
  makeStyles,
  tokens,
} from '@fluentui/react-components';
import { ArrowDownload20Regular, DismissRegular } from '@fluentui/react-icons';
import {
  GEOMETRIA_ESTILOS,
  GEOMETRIA_PARAMETROS,
  GEOMETRIA_FICHA_CAMPOS,
  GEOMETRIA_VIDEO_DURACIONES,
  GEOMETRIA_VIDEO_CALIDADES,
  type GeometriaParam,
  type GetGeometriaResponse,
} from '@supernova/shared';
import { detectarFichaGeometria, generarGeometria, generarRender, getGeometria } from '../api/quoteGeometria.js';
import { IfcViewer3D } from './IfcViewer3D.js';

/**
 * PROTOTIPO staff-only "de la HP a la geometría".
 *
 * Modal que, desde una cotización, genera en $0 un plano 2D y un modelo 3D BIM y
 * —como acción APARTE con costo— fotos fotorrealistas y una animación por IA. Las
 * fotos/video se alimentan de un set AMPLIO de parámetros: unos INFERIDOS de la
 * hoja (la "ficha", editables) y otros MANUALES (definidos en @supernova/shared →
 * GEOMETRIA_PARAMETROS, un solo lugar). Coherente con el resto (primitivos Fluent
 * + tokens/CSS vars). Herramienta INTERNA (staff): el portero real es el backend
 * (flag + requireStaff). Aquí sí se puede hablar de "IA" y de costo.
 */

const useStyles = makeStyles({
  surface: { maxWidth: '1100px', width: '92vw' },
  eyebrow: {
    fontSize: '11px',
    fontWeight: 700,
    letterSpacing: '1.1px',
    textTransform: 'uppercase',
    color: 'var(--privado, ' + tokens.colorPaletteBerryForeground2 + ')',
  },
  viewport: {
    display: 'flex',
    flexDirection: 'column',
    height: '62vh',
    minHeight: '360px',
    background: 'var(--bg-surface)',
    border: '1px solid var(--border-subtle)',
    borderRadius: tokens.borderRadiusMedium,
    overflow: 'hidden',
  },
  center: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '12px',
    padding: '24px',
    textAlign: 'center',
    color: 'var(--text-secondary)',
  },
  imgWrap: {
    flex: 1,
    minHeight: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    overflow: 'auto',
    background: tokens.colorNeutralBackground3,
  },
  img: { maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' },
  video: { maxWidth: '100%', maxHeight: '100%', background: '#000' },
  toolbar: { display: 'flex', gap: '8px', flexWrap: 'wrap', alignItems: 'center' },
  hint: { fontSize: '12px', color: 'var(--text-secondary)', margin: 0 },
  error: { color: 'var(--bad)', fontSize: '13px' },
  // --- formulario de parámetros ---
  form: {
    display: 'flex',
    flexDirection: 'column',
    gap: '14px',
    textAlign: 'left',
    width: '100%',
    maxWidth: '860px',
    margin: '0 auto',
  },
  section: { display: 'flex', flexDirection: 'column', gap: '8px' },
  sectionTitle: {
    fontSize: '11px',
    fontWeight: 700,
    letterSpacing: '0.6px',
    textTransform: 'uppercase',
    color: 'var(--text-secondary)',
    margin: 0,
  },
  grid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))',
    gap: '10px',
  },
  campo: { display: 'flex', flexDirection: 'column', gap: '4px' },
  campoLabel: { fontSize: '12px', fontWeight: 600, color: 'var(--text-primary)' },
  campoAyuda: { fontSize: '11px', color: 'var(--text-secondary)' },
  costRow: {
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: '10px',
    padding: '10px 12px',
    borderRadius: tokens.borderRadiusMedium,
    background: 'var(--bg-surface)',
    border: '1px solid var(--border-subtle)',
  },
  costText: { fontSize: '13px', color: 'var(--text-primary)', margin: 0 },
  costStrong: { fontWeight: 700 },
  // Aviso de sobrescritura (regenerar reemplaza lo anterior, sin historial).
  aviso: {
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
    padding: '10px 12px',
    borderRadius: tokens.borderRadiusMedium,
    background: tokens.colorStatusWarningBackground1,
    border: `1px solid ${tokens.colorStatusWarningBorder1}`,
    color: tokens.colorStatusWarningForeground1,
    fontSize: '13px',
  },
  avisoNota: { fontSize: '11px', color: 'var(--text-secondary)', margin: 0, fontStyle: 'italic' },
});

type Pestana = 'plano' | 'modelo' | 'fotos' | 'animacion';

interface Props {
  quoteId: string;
  quoteName: string;
  open: boolean;
  onClose: () => void;
}

/** Opción "sugerido por IA" del selector de estilo (usa ficha.estilo_sugerido). */
const ESTILO_IA = '__ia__';

/** Selección inicial de los parámetros manuales = su valor por defecto. */
function parametrosPorDefecto(): Record<string, string> {
  const o: Record<string, string> = {};
  for (const p of GEOMETRIA_PARAMETROS) o[p.id] = p.predeterminado;
  return o;
}

function money(n: number): string {
  return `$${n.toFixed(n < 1 ? 3 : 2)}`;
}

export function GeometriaHpPanel({ quoteId, quoteName, open, onClose }: Props) {
  const styles = useStyles();
  const [geo, setGeo] = useState<GetGeometriaResponse | null>(null);
  const [cargando, setCargando] = useState(false);
  const [detectandoFicha, setDetectandoFicha] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [pestana, setPestana] = useState<Pestana>('plano');

  // --- selección del usuario ---
  const [estilo, setEstilo] = useState<string>(GEOMETRIA_ESTILOS[0].id);
  const [parametros, setParametros] = useState<Record<string, string>>(parametrosPorDefecto);
  const [fichaEdits, setFichaEdits] = useState<Record<string, string>>({});
  const [conVideo, setConVideo] = useState(false);
  const [duracionS, setDuracionS] = useState<number>(GEOMETRIA_VIDEO_DURACIONES[1] ?? 6);
  const [calidad, setCalidad] = useState<string>('fast');
  const [confirmar, setConfirmar] = useState(false);
  // Confirmación aparte para "Volver a generar plano/3D" (sobrescribe la geometría base).
  const [confirmarBase, setConfirmarBase] = useState(false);

  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const detenerPoll = useCallback(() => {
    if (pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
  }, []);

  const refrescar = useCallback(async () => {
    try {
      const r = await getGeometria(quoteId);
      setGeo(r);
      setError(r.error ?? r.renderError);
      if (r.status !== 'running' && r.renderStatus !== 'running') detenerPoll();
      return r;
    } catch (e) {
      setError(e instanceof Error ? e.message : 'No se pudo consultar el estado.');
      detenerPoll();
      return null;
    }
  }, [quoteId, detenerPoll]);

  // Detección de la ficha (IA ligera, cacheada en el server): prellena los campos
  // inferidos. Best-effort: si falla, quedan vacíos y editables (no molesta al user).
  // Prellena vía geo.ficha (no fichaEdits) para que el usuario pueda sobreescribir.
  const detectarFicha = useCallback(async () => {
    setDetectandoFicha(true);
    try {
      const r = await detectarFichaGeometria(quoteId);
      if (r.ficha) setGeo((g) => (g ? { ...g, ficha: r.ficha } : g));
    } catch {
      /* sin ficha: los campos quedan vacíos y editables */
    } finally {
      setDetectandoFicha(false);
    }
  }, [quoteId]);

  // Al abrir: consultar estado; si algo está corriendo, seguir con polling; y
  // detectar la ficha para prellenar (solo si aún no está detectada).
  useEffect(() => {
    if (!open) return;
    setError(null);
    void (async () => {
      const r = await refrescar();
      if ((r?.status === 'running' || r?.renderStatus === 'running') && !pollRef.current) {
        pollRef.current = setInterval(() => void refrescar(), 2500);
      }
      if (r && !r.ficha) void detectarFicha();
    })();
    return () => detenerPoll();
  }, [open, refrescar, detenerPoll, detectarFicha]);

  const arrancarPoll = useCallback(() => {
    detenerPoll();
    pollRef.current = setInterval(() => void refrescar(), 2500);
  }, [detenerPoll, refrescar]);

  const generar = useCallback(async () => {
    setCargando(true);
    setError(null);
    try {
      await generarGeometria(quoteId);
      setGeo((g) => ({
        ...(g ?? {
          status: 'running', dxfUrl: null, svgUrl: null, ifcUrl: null, renderUrl: null, timingsMs: null, error: null,
          renderStatus: 'none', renderError: null, renderEstilo: null, videoUrl: null, ficha: null, renderCostoUsd: null,
          videoDisponible: false,
          precios: { fotoUsd: 0.039, videoFastUsdPorSeg: 0.1, videoLiteUsdPorSeg: null, duracionesS: [4, 6, 8] },
        }),
        status: 'running', error: null, dxfUrl: null, svgUrl: null, ifcUrl: null,
      }));
      arrancarPoll();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'No se pudo iniciar la generación.');
    } finally {
      setCargando(false);
    }
  }, [quoteId, arrancarPoll]);

  // Valor efectivo de un campo de la ficha: lo que editó el usuario o lo detectado.
  const fichaValor = useCallback(
    (id: string): string => {
      if (fichaEdits[id] !== undefined) return fichaEdits[id];
      const det = geo?.ficha ? (geo.ficha as unknown as Record<string, string | null>)[id] : null;
      return det ?? '';
    },
    [fichaEdits, geo],
  );

  const generarFotos = useCallback(async () => {
    setConfirmar(false);
    setCargando(true);
    setError(null);
    const estiloFinal =
      estilo === ESTILO_IA ? (geo?.ficha?.estilo_sugerido || GEOMETRIA_ESTILOS[0].id) : estilo;
    // Overrides de la ficha: los valores efectivos no vacíos (el worker los mezcla
    // encima de lo que deduce la IA).
    const fichaOverride: Record<string, string> = {};
    for (const campo of GEOMETRIA_FICHA_CAMPOS) {
      const v = fichaValor(campo.id).trim();
      if (v) fichaOverride[campo.id] = v;
    }
    try {
      await generarRender(quoteId, {
        estilo: estiloFinal,
        video: conVideo && (geo?.videoDisponible ?? false),
        parametros,
        ficha: fichaOverride,
        duracionS,
        calidad,
      });
      setGeo((g) => (g ? { ...g, renderStatus: 'running', renderError: null } : g));
      arrancarPoll();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'No se pudo iniciar el render.');
    } finally {
      setCargando(false);
    }
  }, [quoteId, estilo, parametros, duracionS, calidad, conVideo, geo, fichaValor, arrancarPoll]);

  const baseCorriendo = geo?.status === 'running';
  const baseListo = geo?.status === 'completed';
  const renderCorriendo = geo?.renderStatus === 'running';
  const renderListo = geo?.renderStatus === 'completed';
  const videoDisponible = geo?.videoDisponible ?? false;
  const hayTabs = baseListo || renderListo;

  // ¿Qué hay YA generado? Regenerar SOBREESCRIBE (una sola versión por cotización,
  // sin historial): estos flags deciden si mostramos el aviso de "descárgalo antes".
  const hayFoto = Boolean(geo?.renderUrl);
  const hayVideo = Boolean(geo?.videoUrl);
  const hayBase = Boolean(geo?.dxfUrl || geo?.svgUrl || geo?.ifcUrl);

  // Precios (para estimar el costo). Fallback defensivo si el server no los mandó.
  const precios = geo?.precios ?? {
    fotoUsd: 0.039,
    videoFastUsdPorSeg: 0.1,
    videoLiteUsdPorSeg: null as number | null,
    duracionesS: [...GEOMETRIA_VIDEO_DURACIONES] as number[],
  };
  const liteDisponible = precios.videoLiteUsdPorSeg != null;
  const tarifaVideo =
    calidad === 'lite' && precios.videoLiteUsdPorSeg != null
      ? precios.videoLiteUsdPorSeg
      : precios.videoFastUsdPorSeg;
  const costoVideo = conVideo && videoDisponible ? duracionS * tarifaVideo : 0;
  const costoTotal = precios.fotoUsd + costoVideo;

  const cerrar = () => {
    detenerPoll();
    onClose();
  };

  // --- render de un parámetro manual (single o multi) ---
  const setParam = (id: string, valor: string) => setParametros((p) => ({ ...p, [id]: valor }));

  const renderParam = (p: GeometriaParam) => {
    const actual = parametros[p.id] ?? p.predeterminado;
    if (p.multi) {
      const seleccion = actual.split(',').map((s) => s.trim()).filter(Boolean);
      const etiqueta =
        seleccion.length === 0
          ? 'Ninguno'
          : p.opciones.filter((o) => seleccion.includes(o.id)).map((o) => o.label).join(', ');
      return (
        <div className={styles.campo} key={p.id}>
          <span className={styles.campoLabel}>{p.label}</span>
          <Dropdown
            multiselect
            aria-label={p.label}
            value={etiqueta}
            selectedOptions={seleccion}
            onOptionSelect={(_, d) => setParam(p.id, (d.selectedOptions ?? []).join(','))}
          >
            {p.opciones.map((o) => (
              <Option key={o.id} value={o.id}>{o.label}</Option>
            ))}
          </Dropdown>
          {p.ayuda && <span className={styles.campoAyuda}>{p.ayuda}</span>}
        </div>
      );
    }
    return (
      <div className={styles.campo} key={p.id}>
        <span className={styles.campoLabel}>{p.label}</span>
        <Dropdown
          aria-label={p.label}
          value={p.opciones.find((o) => o.id === actual)?.label ?? actual}
          selectedOptions={[actual]}
          onOptionSelect={(_, d) => d.optionValue && setParam(p.id, d.optionValue)}
        >
          {p.opciones.map((o) => (
            <Option key={o.id} value={o.id}>{o.label}</Option>
          ))}
        </Dropdown>
        {p.ayuda && <span className={styles.campoAyuda}>{p.ayuda}</span>}
      </div>
    );
  };

  const paramsFoto = useMemo(() => GEOMETRIA_PARAMETROS.filter((p) => p.ambito === 'foto'), []);
  const paramsVideo = useMemo(() => GEOMETRIA_PARAMETROS.filter((p) => p.ambito === 'video'), []);

  // --- formulario completo de generación de fotos/animación ---
  const formulario = (
    <div className={styles.form}>
      {/* Datos inferidos (editables) */}
      <div className={styles.section}>
        <p className={styles.sectionTitle}>Datos de la obra (detectados de la hoja · editables)</p>
        {detectandoFicha && (
          <Spinner size="tiny" labelPosition="after" label="Detectando datos de la hoja…" />
        )}
        <div className={styles.grid}>
          {GEOMETRIA_FICHA_CAMPOS.map((campo) => (
            <div className={styles.campo} key={campo.id}>
              <span className={styles.campoLabel}>{campo.label}</span>
              <Input
                aria-label={campo.label}
                placeholder={campo.placeholder}
                value={fichaValor(campo.id)}
                onChange={(_, d) => setFichaEdits((f) => ({ ...f, [campo.id]: d.value }))}
              />
            </div>
          ))}
        </div>
      </div>

      {/* Estilo + parámetros creativos de la foto */}
      <div className={styles.section}>
        <p className={styles.sectionTitle}>Estilo, composición y ambiente</p>
        <div className={styles.grid}>
          <div className={styles.campo}>
            <span className={styles.campoLabel}>Estilo arquitectónico</span>
            <Dropdown
              aria-label="Estilo arquitectónico"
              value={
                estilo === ESTILO_IA
                  ? `Sugerido por IA${geo?.ficha?.estilo_sugerido ? ` (${geo.ficha.estilo_sugerido})` : ''}`
                  : (GEOMETRIA_ESTILOS.find((e) => e.id === estilo)?.label ?? estilo)
              }
              selectedOptions={[estilo]}
              onOptionSelect={(_, d) => d.optionValue && setEstilo(d.optionValue)}
            >
              {GEOMETRIA_ESTILOS.map((e) => (
                <Option key={e.id} value={e.id}>{e.label}</Option>
              ))}
              {geo?.ficha?.estilo_sugerido ? (
                <Option value={ESTILO_IA}>{`Sugerido por IA (${geo.ficha.estilo_sugerido})`}</Option>
              ) : null}
            </Dropdown>
          </div>
          {paramsFoto.map(renderParam)}
        </div>
      </div>

      {/* Animación (solo si el servidor la tiene habilitada) */}
      {videoDisponible && (
        <div className={styles.section}>
          <p className={styles.sectionTitle}>Animación (video)</p>
          <Checkbox
            label="Incluir animación (video)"
            checked={conVideo}
            onChange={(_, d) => setConVideo(Boolean(d.checked))}
          />
          {conVideo && (
            <div className={styles.grid}>
              {paramsVideo.map(renderParam)}
              <div className={styles.campo}>
                <span className={styles.campoLabel}>Duración</span>
                <Dropdown
                  aria-label="Duración del video"
                  value={`${duracionS} s`}
                  selectedOptions={[String(duracionS)]}
                  onOptionSelect={(_, d) => d.optionValue && setDuracionS(Number(d.optionValue))}
                >
                  {(precios.duracionesS ?? GEOMETRIA_VIDEO_DURACIONES).map((s) => (
                    <Option key={s} value={String(s)}>
                      {`${s} s — ${money(s * tarifaVideo)}`}
                    </Option>
                  ))}
                </Dropdown>
              </div>
              <div className={styles.campo}>
                <span className={styles.campoLabel}>Calidad</span>
                <Dropdown
                  aria-label="Calidad del video"
                  value={GEOMETRIA_VIDEO_CALIDADES.find((c) => c.id === calidad)?.label ?? calidad}
                  selectedOptions={[calidad]}
                  onOptionSelect={(_, d) => d.optionValue && setCalidad(d.optionValue)}
                >
                  {GEOMETRIA_VIDEO_CALIDADES.filter((c) => c.id !== 'lite' || liteDisponible).map((c) => {
                    const tarifa = c.id === 'lite' ? precios.videoLiteUsdPorSeg : precios.videoFastUsdPorSeg;
                    return (
                      <Option key={c.id} value={c.id}>
                        {`${c.label}${tarifa != null ? ` — ${money(tarifa)}/s` : ''}`}
                      </Option>
                    );
                  })}
                </Dropdown>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Costo estimado + acción */}
      <div className={styles.costRow}>
        <p className={styles.costText}>
          Costo estimado:{' '}
          <span className={styles.costStrong}>{money(costoTotal)}</span>{' '}
          <span className={styles.campoAyuda}>
            (foto {money(precios.fotoUsd)}
            {conVideo && videoDisponible ? ` + animación ${money(costoVideo)}` : ''})
          </span>
        </p>
        <Button appearance="primary" disabled={cargando} onClick={() => setConfirmar(true)}>
          Generar fotos y animación (con costo)
        </Button>
      </div>
    </div>
  );

  // Guardia dura: si está cerrado, NO renderizar nada (evita que el <Dialog> de Fluent
  // muestre la superficie con open=false, que era la causa de que se abriera solo).
  if (!open) return null;

  return (
    <Dialog open={open} onOpenChange={(_, d) => { if (!d.open) cerrar(); }}>
      <DialogSurface className={styles.surface}>
        <DialogBody>
          <DialogTitle
            action={<Button appearance="subtle" icon={<DismissRegular />} aria-label="Cerrar" onClick={cerrar} />}
          >
            <span className={styles.eyebrow}>Interno · Staff</span>
            <div>Plano, modelo 3D e imágenes desde la hoja</div>
          </DialogTitle>
          <DialogContent>
            <p className={styles.hint}>
              Prototipo en evaluación: a partir de los conceptos de <strong>{quoteName}</strong> se
              generan un plano 2D y un modelo 3D BIM en $0. Las <strong>fotos</strong> y la{' '}
              <strong>animación</strong> se generan con IA (tienen costo) usando los datos detectados
              de la hoja más los parámetros que elijas abajo.
            </p>

            {error && <p className={styles.error}>{error}</p>}

            {!hayTabs && (
              <div className={styles.viewport} style={{ height: 'auto', minHeight: 0, overflow: 'auto' }}>
                <div className={styles.center} style={{ justifyContent: 'flex-start' }}>
                  {baseCorriendo || renderCorriendo ? (
                    <>
                      <Spinner size="large" label={renderCorriendo ? 'Generando fotos y animación…' : 'Generando plano y modelo 3D…'} />
                      <p className={styles.hint}>Esto puede tardar unos minutos. Puedes dejar la ventana abierta.</p>
                    </>
                  ) : (
                    <>
                      <p className={styles.hint}>
                        {geo?.status === 'failed' || geo?.renderStatus === 'failed'
                          ? 'La última generación no se pudo completar.'
                          : 'Todavía no se ha generado nada para esta cotización.'}
                      </p>
                      <div className={styles.toolbar} style={{ justifyContent: 'center' }}>
                        <Button appearance="primary" disabled={cargando} onClick={() => void generar()}>
                          {geo?.status === 'failed' ? 'Reintentar plano y modelo 3D' : 'Generar plano y modelo 3D ($0)'}
                        </Button>
                      </div>
                      <p className={styles.hint}>o genera directamente las imágenes por IA:</p>
                      {formulario}
                    </>
                  )}
                </div>
              </div>
            )}

            {hayTabs && geo && (
              <>
                <TabList selectedValue={pestana} onTabSelect={(_, d) => setPestana(d.value as Pestana)}>
                  <Tab value="plano">Plano 2D</Tab>
                  <Tab value="modelo">Modelo 3D</Tab>
                  <Tab value="fotos">Fotos</Tab>
                  <Tab value="animacion">Animación</Tab>
                </TabList>

                <div className={styles.viewport} style={{ marginTop: 8 }}>
                  {pestana === 'plano' &&
                    (geo.svgUrl ? (
                      <div className={styles.imgWrap}>
                        <img className={styles.img} src={geo.svgUrl} alt="Plano 2D generado" />
                      </div>
                    ) : (
                      <div className={styles.center}>No se produjo el plano 2D.</div>
                    ))}

                  {pestana === 'modelo' &&
                    (geo.ifcUrl ? (
                      <IfcViewer3D srcUrl={geo.ifcUrl} />
                    ) : (
                      <div className={styles.center}>No se produjo el modelo 3D.</div>
                    ))}

                  {pestana === 'fotos' &&
                    (renderCorriendo ? (
                      <div className={styles.center}>
                        <Spinner size="large" label="Generando fotos…" />
                      </div>
                    ) : geo.renderUrl ? (
                      <div className={styles.imgWrap} style={{ flexDirection: 'column' }}>
                        <img className={styles.img} src={geo.renderUrl} alt="Foto generada" />
                      </div>
                    ) : (
                      <div className={styles.center} style={{ justifyContent: 'flex-start', overflow: 'auto' }}>
                        <p className={styles.hint}>Aún no hay fotos. Ajusta los parámetros y genera.</p>
                        {formulario}
                      </div>
                    ))}

                  {pestana === 'animacion' &&
                    (!videoDisponible ? (
                      <div className={styles.center}>
                        <p className={styles.hint}>
                          Animación no disponible en este entorno. Se habilita desde configuración
                          cuando se apruebe.
                        </p>
                      </div>
                    ) : renderCorriendo ? (
                      <div className={styles.center}>
                        <Spinner size="large" label="Generando animación…" />
                      </div>
                    ) : geo.videoUrl ? (
                      <div className={styles.imgWrap}>
                        <video className={styles.video} src={geo.videoUrl} controls autoPlay loop muted />
                      </div>
                    ) : (
                      <div className={styles.center}>
                        <p className={styles.hint}>
                          Aún no hay animación. Genera desde la pestaña Fotos activando “Incluir
                          animación”.
                        </p>
                      </div>
                    ))}
                </div>
              </>
            )}
          </DialogContent>
          <DialogActions>
            <div className={styles.toolbar}>
              {baseListo && geo?.dxfUrl && (
                <Button as="a" href={geo.dxfUrl} download={`${quoteName}.dxf`} icon={<ArrowDownload20Regular />} appearance="secondary">
                  Descargar plano (DXF)
                </Button>
              )}
              {baseListo && geo?.ifcUrl && (
                <Button as="a" href={geo.ifcUrl} download={`${quoteName}.ifc`} icon={<ArrowDownload20Regular />} appearance="secondary">
                  Descargar modelo (IFC)
                </Button>
              )}
              {renderListo && geo?.renderUrl && (
                <Button as="a" href={geo.renderUrl} download={`${quoteName}.png`} icon={<ArrowDownload20Regular />} appearance="secondary">
                  Descargar foto
                </Button>
              )}
              {renderListo && geo?.videoUrl && (
                <Button as="a" href={geo.videoUrl} download={`${quoteName}.mp4`} icon={<ArrowDownload20Regular />} appearance="secondary">
                  Descargar animación
                </Button>
              )}
              {baseListo && (
                <Button
                  appearance="subtle"
                  disabled={cargando || baseCorriendo}
                  onClick={() => setConfirmarBase(true)}
                >
                  Volver a generar plano/3D
                </Button>
              )}
            </div>
          </DialogActions>
        </DialogBody>
      </DialogSurface>

      {/* Diálogos de confirmación (envueltos en un fragmento para que el <Dialog> exterior
          siga teniendo exactamente 2 hijos: DialogSurface + este bloque). */}
      <>
      {/* Confirmación de costo del render por IA */}
      <Dialog open={confirmar} onOpenChange={(_, d) => setConfirmar(d.open)}>
        <DialogSurface>
          <DialogBody>
            <DialogTitle>Generar imágenes por IA (con costo)</DialogTitle>
            <DialogContent>
              {/* Aviso de sobrescritura: solo si YA hay foto/animación que se perdería.
                  TEXTO PROVISIONAL (borrador) — Sebastián redacta el final. */}
              {(hayFoto || hayVideo) && (
                <div className={styles.aviso}>
                  <span>
                    <strong>Ya hay {hayFoto && hayVideo ? 'una foto y una animación' : hayFoto ? 'una foto' : 'una animación'} generadas.</strong>{' '}
                    Al continuar se <strong>reemplazan</strong> por las nuevas y no se guarda la
                    versión anterior. Si quieres conservarlas, usa los botones{' '}
                    <strong>Descargar</strong> antes de regenerar.
                  </span>
                  <p className={styles.avisoNota}>Texto provisional.</p>
                </div>
              )}
              <p className={styles.hint}>
                Se generará una <strong>foto fotorrealista</strong> del estilo elegido
                {conVideo && videoDisponible ? ' y un clip de animación' : ''}, usando IA de pago.
                Costo estimado: <strong>{money(costoTotal)}</strong>
                {conVideo && videoDisponible
                  ? ` (foto ${money(precios.fotoUsd)} + animación ${money(costoVideo)} por ${duracionS} s).`
                  : ` (una foto).`}{' '}
                ¿Continuar?
              </p>
            </DialogContent>
            <DialogActions>
              <Button appearance="secondary" onClick={() => setConfirmar(false)}>Cancelar</Button>
              <Button appearance="primary" onClick={() => void generarFotos()}>Sí, generar</Button>
            </DialogActions>
          </DialogBody>
        </DialogSurface>
      </Dialog>

      {/* Confirmación de volver a generar el plano/modelo 3D (sobrescribe la geometría base).
          Sin costo, pero igual reemplaza dxf/svg/ifc sin historial. */}
      <Dialog open={confirmarBase} onOpenChange={(_, d) => setConfirmarBase(d.open)}>
        <DialogSurface>
          <DialogBody>
            <DialogTitle>Volver a generar plano y modelo 3D</DialogTitle>
            <DialogContent>
              {/* Aviso de sobrescritura. TEXTO PROVISIONAL (borrador) — Sebastián redacta el final. */}
              {hayBase && (
                <div className={styles.aviso}>
                  <span>
                    <strong>Se reemplazan el plano y el modelo 3D actuales.</strong> No se guarda la
                    versión anterior. Si quieres conservarlos, usa los botones{' '}
                    <strong>Descargar plano (DXF)</strong> y <strong>Descargar modelo (IFC)</strong>{' '}
                    antes de regenerar.
                  </span>
                  <p className={styles.avisoNota}>Texto provisional.</p>
                </div>
              )}
              <p className={styles.hint}>
                Se generan de nuevo el plano 2D y el modelo 3D a partir de los conceptos de la hoja
                (sin costo). ¿Continuar?
              </p>
            </DialogContent>
            <DialogActions>
              <Button appearance="secondary" onClick={() => setConfirmarBase(false)}>Cancelar</Button>
              <Button
                appearance="primary"
                onClick={() => {
                  setConfirmarBase(false);
                  void generar();
                }}
              >
                Sí, regenerar
              </Button>
            </DialogActions>
          </DialogBody>
        </DialogSurface>
      </Dialog>
      </>
    </Dialog>
  );
}
